<script>
  import { onMount, onDestroy } from 'svelte';
  import { t } from 'svelte-i18n';
  import { fetchActivoReportData, exportActivoReportToCSV, downloadCSV, fetchFilterOptions } from '../controllers/rptActivoController.js';

  let reportData = [];
  let filteredData = [];
  let loading = false;
  let totalSum = 0;
  let chartInstance = null;
  let chartCanvas;

  // Filter options
  let tipos = [];
  let selectedTipo = '';

  // Expandable state
  let expandedTipos = {};
  let updateTrigger = 0;

  // Fetch report data
  async function fetchReportData() {
    loading = true;
    try {
      const result = await fetchActivoReportData();
      reportData = result.reportData;
      filteredData = result.reportData;
      totalSum = result.totalSum;
      
      // Initialize expanded states
      expandedTipos = {};
      reportData.forEach(tipo => {
        expandedTipos[tipo.tipoId] = false;
      });
      
      // Fetch filter options
      const filterOptions = await fetchFilterOptions();
      tipos = filterOptions.tipos;
      
      // Update chart
      setTimeout(() => {
        updateChart();
      }, 100);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      loading = false;
    }
  }

  // Apply filters
  function applyFilters() {
    filteredData = reportData.filter(tipo => {
      return !selectedTipo || tipo.tipoId === selectedTipo;
    });
    
    totalSum = filteredData.reduce((sum, tipo) => sum + tipo.totalPrecio, 0);
    
    setTimeout(() => {
      updateChart();
    }, 100);
  }

  // Clear filters
  function clearFilters() {
    selectedTipo = '';
    applyFilters();
  }

  // Toggle tipo expansion
  function toggleTipo(tipoId) {
    console.log('Toggling tipo:', tipoId, 'Current state:', expandedTipos[tipoId]);
    expandedTipos = { ...expandedTipos, [tipoId]: !expandedTipos[tipoId] };
    updateTrigger++;
    console.log('New state:', expandedTipos[tipoId], 'Trigger:', updateTrigger);
    
    // Debug: Check activos data
    const tipo = filteredData.find(t => t.tipoId === tipoId);
    if (tipo) {
      console.log('Tipo activos:', tipo.activos);
      console.log('Activos length:', tipo.activos.length);
    }
  }

  // Check if tipo is expanded
  function isTipoExpanded(tipoId) {
    return expandedTipos[tipoId] || false;
  }

  // Expand all tipos
  function expandAllTipos() {
    const newStates = {};
    filteredData.forEach(tipo => {
      newStates[tipo.tipoId] = true;
    });
    expandedTipos = newStates;
    updateTrigger++;
  }

  // Collapse all tipos
  function collapseAllTipos() {
    const newStates = {};
    filteredData.forEach(tipo => {
      newStates[tipo.tipoId] = false;
    });
    expandedTipos = newStates;
    updateTrigger++;
  }

  // Update pie chart
  function updateChart() {
    if (chartInstance) {
      chartInstance.destroy();
    }

    if (!chartCanvas || !window.Chart) return;

    const ctx = chartCanvas.getContext('2d');
    
    const chartData = filteredData.map(tipo => ({
      label: tipo.tipoDescrip,
      value: tipo.totalPrecio,
      color: getRandomColor()
    }));

    chartInstance = new window.Chart(ctx, {
      type: 'pie',
      data: {
        labels: chartData.map(item => item.label),
        datasets: [{
          data: chartData.map(item => item.value),
          backgroundColor: chartData.map(item => item.color),
          borderColor: '#fff',
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || '';
                const value = context.parsed;
                const percentage = ((value / totalSum) * 100).toFixed(1);
                return `${label}: $${value.toLocaleString()} (${percentage}%)`;
              }
            }
          }
        }
      }
    });
  }

  // Generate random color
  function getRandomColor() {
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  // Export to CSV
  function exportToCSV() {
    const csvContent = exportActivoReportToCSV(filteredData);
    downloadCSV(csvContent, 'reporte_activo_precio.csv');
  }

  // Reactive statement to force updates
  $: updateTrigger, expandedTipos;

  onMount(() => {
    fetchReportData();
  });

  onDestroy(() => {
    if (chartInstance) {
      chartInstance.destroy();
    }
  });
</script>

<div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
  <!-- Header -->
  <div class="rounded-t mb-0 px-4 py-3 bg-transparent">
    <div class="flex flex-wrap items-center">
      <div class="relative w-full px-4 max-w-full flex-grow flex-1">
        <h3 class="font-semibold text-base text-blueGray-700">
          Reporte de Activos - Suma de Precios por Tipo
        </h3>
      </div>
      <div class="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
        <button
          on:click={fetchReportData}
          class="bg-blueGray-500 text-white active:bg-blueGray-600 text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
          type="button"
        >
          <i class="fas fa-sync-alt mr-1"></i>
          Actualizar
        </button>
        <button
          on:click={exportToCSV}
          class="bg-green-500 text-white active:bg-green-600 text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
          type="button"
        >
          <i class="fas fa-download mr-1"></i>
          Exportar CSV
        </button>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="px-4 py-3 bg-blueGray-50 border-b border-blueGray-200">
    <div class="flex flex-wrap items-center gap-4">
      <div class="flex items-center gap-2">
        <label for="tipo-filter" class="text-sm font-medium text-blueGray-700">Filtrar por Tipo:</label>
        <select
          id="tipo-filter"
          bind:value={selectedTipo}
          on:change={applyFilters}
          class="px-3 py-1 border border-blueGray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos los tipos</option>
          {#each tipos as tipo}
            <option value={tipo.value}>{tipo.label}</option>
          {/each}
        </select>
      </div>
      
      <button
        on:click={clearFilters}
        class="bg-gray-500 text-white text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none hover:bg-gray-600 ease-linear transition-all duration-150"
        type="button"
      >
        <i class="fas fa-times mr-1"></i>
        Limpiar Filtros
      </button>
      
      <div class="flex gap-2">
        <button
          on:click={expandAllTipos}
          class="bg-blue-500 text-white text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none hover:bg-blue-600 ease-linear transition-all duration-150"
          type="button"
        >
          <i class="fas fa-expand-arrows-alt mr-1"></i>
          Expandir Todo
        </button>
        <button
          on:click={collapseAllTipos}
          class="bg-orange-500 text-white text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none hover:bg-orange-600 ease-linear transition-all duration-150"
          type="button"
        >
          <i class="fas fa-compress-arrows-alt mr-1"></i>
          Colapsar Todo
        </button>
      </div>
    </div>
  </div>

  <div class="block w-full overflow-x-auto">
    {#if loading}
      <div class="text-center py-8">
        <i class="fas fa-spinner fa-spin text-2xl text-blueGray-500"></i>
        <p class="mt-2 text-blueGray-500">Cargando datos del reporte...</p>
      </div>
    {:else if reportData.length === 0}
      <div class="text-center py-8">
        <i class="fas fa-chart-bar text-4xl text-blueGray-300"></i>
        <p class="mt-2 text-blueGray-500">No hay datos para mostrar</p>
      </div>
    {:else}
      <!-- Summary Card -->
      <div class="bg-blueGray-50 p-4 mb-4">
        <div class="flex justify-between items-center">
          <h4 class="text-lg font-semibold text-blueGray-700">Resumen General</h4>
          <div class="text-right">
            <p class="text-2xl font-bold text-blueGray-800">${totalSum.toLocaleString()}</p>
            <p class="text-sm text-blueGray-600">Total de Precios</p>
          </div>
        </div>
      </div>

      <!-- Chart Section -->
      <div class="bg-white p-4 mb-4 border border-blueGray-200 rounded-lg">
        <h4 class="text-lg font-semibold text-blueGray-700 mb-4">Distribución por Tipo</h4>
        <div class="relative" style="height: 400px;">
          <canvas bind:this={chartCanvas}></canvas>
        </div>
      </div>

      <!-- Tipo Groups -->
      {#each filteredData as tipo}
        <div class="mb-4 border border-blueGray-200 rounded-lg">
          <!-- Tipo Header -->
          <div 
            class="bg-blueGray-100 px-4 py-3 border-b border-blueGray-200 cursor-pointer hover:bg-blueGray-200 transition-colors duration-200"
            on:click={() => toggleTipo(tipo.tipoId)}
            role="button"
            tabindex="0"
            on:keydown={(e) => e.key === 'Enter' && toggleTipo(tipo.tipoId)}
          >
            <div class="flex justify-between items-center">
              <div class="flex items-center">
                <i class="fas {isTipoExpanded(tipo.tipoId) ? 'fa-chevron-down' : 'fa-chevron-right'} mr-3 text-blueGray-600 transition-transform duration-200"></i>
                <h4 class="text-lg font-semibold text-blueGray-800">{tipo.tipoDescrip}</h4>
                <span class="ml-3 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  {tipo.activosCount} activo{tipo.activosCount !== 1 ? 's' : ''}
                </span>
              </div>
              <div class="text-right">
                <p class="text-2xl font-bold text-green-600">${tipo.totalPrecio.toLocaleString()}</p>
                <p class="text-sm text-blueGray-600">Total del Tipo</p>
              </div>
            </div>
          </div>

          <!-- Activos Details -->
          {#if isTipoExpanded(tipo.tipoId) && updateTrigger >= 0}
            <div class="p-4">
              <!-- Debug info -->
              <div class="mb-2 p-2 bg-yellow-100 text-xs">
                Debug: Activos count: {tipo.activosCount}, Tipo: {tipo.tipoDescrip}
              </div>
              {#if tipo.activosCount > 0}
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-blueGray-200">
                    <thead class="bg-blueGray-50">
                      <tr>
                        <th class="px-3 py-2 text-left text-xs font-medium text-blueGray-500 uppercase tracking-wider">
                          Código
                        </th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-blueGray-500 uppercase tracking-wider">
                          Nombre
                        </th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-blueGray-500 uppercase tracking-wider">
                          Descripción
                        </th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-blueGray-500 uppercase tracking-wider">
                          Precio
                        </th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-blueGray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-blueGray-500 uppercase tracking-wider">
                          Fecha Creación
                        </th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-blueGray-200">
                      {#each tipo.activos as activo}
                        <tr class="hover:bg-blueGray-50">
                          <td class="px-3 py-2 whitespace-nowrap text-sm font-medium text-blueGray-900">
                            {activo.codigo}
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap text-sm font-medium text-blueGray-900">
                            {activo.nombre}
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap text-sm text-blueGray-900">
                            {activo.descripcion}
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap text-sm font-semibold text-green-600">
                            ${activo.precio.toLocaleString()}
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap text-sm text-blueGray-900">
                            <span class="px-2 py-1 text-xs font-medium rounded-full {activo.estado === 'Activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}">
                              {activo.estado}
                            </span>
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap text-sm text-blueGray-900">
                            {new Date(activo.fechaCreacion).toLocaleDateString()}
                          </td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {:else}
                <div class="p-4 text-center text-blueGray-500">
                  <i class="fas fa-info-circle text-2xl mb-2"></i>
                  <p>No hay activos disponibles para este tipo</p>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    {/if}
  </div>
</div>